import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  listTimelineClips,
  syncTimelineFromScenes,
  rebuildTimeline,
  updateClip,
  deleteClip,
  addBlankClip,
  persistClipOrder,
  formatDuration,
  type ContentClip,
  type ContentScene,
} from '@/lib/contentStudio';

interface Props {
  projectId: number;
  scenes: ContentScene[];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Timeline de montage : chaque scène devient un clip. On peut réordonner
 * (avec les flèches), couper (rentrer/sortir la tête et la queue), dupliquer,
 * supprimer, ou repartir d'un montage reconstruit depuis les scènes.
 */
export default function TimelineEditor({ projectId, scenes }: Props) {
  const [clips, setClips] = useState<ContentClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTimelineClips(projectId);
      setClips(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const sceneLabel = (sceneId: number | null): string => {
    if (sceneId == null) return 'Écran-titre';
    const s = scenes.find((x) => x.id === sceneId);
    return s ? `Scène ${pad(s.number)}` : 'Scène supprimée';
  };

  const totalMs = clips.reduce((acc, c) => acc + (c.duration_ms || 0), 0);

  const handleRebuild = async () => {
    setBusy(true);
    setError(null);
    try {
      const list = await rebuildTimeline(projectId, scenes);
      setClips(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const list = await syncTimelineFromScenes(projectId, scenes);
      setClips(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= clips.length) return;
    const next = [...clips];
    [next[index], next[target]] = [next[target], next[index]];
    setClips(next);
    try {
      await persistClipOrder(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
      await load();
    }
  };

  const applyTrim = async (clip: ContentClip, startMs: number, endMs: number) => {
    const start = Math.max(0, Math.round(startMs));
    const end = Math.max(start + 200, Math.round(endMs));
    setBusy(true);
    try {
      await updateClip(clip.id, { start_ms: start, end_ms: end, duration_ms: end - start });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (clip: ContentClip, index: number) => {
    setBusy(true);
    try {
      const { error: insErr } = await supabase.from('content_timeline_clips').insert({
        project_id: projectId,
        scene_id: clip.scene_id,
        position: index + 1,
        start_ms: clip.start_ms,
        end_ms: clip.end_ms,
        duration_ms: clip.duration_ms,
      });
      if (insErr) throw insErr;
      const list = await listTimelineClips(projectId);
      setClips(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (clip: ContentClip) => {
    try {
      await deleteClip(clip.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const handleAddBlank = async () => {
    try {
      await addBlankClip(projectId, clips.length);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-movie-2-line text-foreground-500"></i>Timeline de montage
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Réordonnez, coupez ou dupliquez les scènes. Le montage suit l&apos;ordre affiché.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSync}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Ajouter les scènes manquantes
          </button>
          <button
            type="button"
            onClick={handleRebuild}
            disabled={busy || scenes.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>Reconstruire depuis les scènes
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Bande de montage (aperçu proportionnel) */}
      {clips.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-medium text-foreground-500 flex items-center gap-1">
              <i className="ri-film-line"></i>Aperçu du montage
            </span>
            <span className="text-[11px] text-foreground-500">Durée totale : <strong className="text-foreground-800">{formatDuration(Math.round(totalMs / 1000))}</strong></span>
          </div>
          <div className="flex h-12 rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
            {clips.map((c, i) => (
              <div
                key={c.id}
                style={{ flexGrow: Math.max(1, c.duration_ms || 1000) }}
                className={`relative min-w-[28px] flex items-center justify-center text-[10px] font-semibold text-background-50 border-r border-background-50/40 last:border-r-0 ${
                  i % 2 === 0 ? 'bg-primary-500' : 'bg-accent-500'
                }`}
                title={`${sceneLabel(c.scene_id)} · ${formatDuration(Math.round((c.duration_ms || 0) / 1000))}`}
              >
                {pad(i + 1)}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : clips.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-movie-2-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Timeline vide</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs mb-4">
            Générez les scènes puis reconstruisez la timeline pour les placer automatiquement.
          </p>
          <button
            type="button"
            onClick={handleRebuild}
            disabled={scenes.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            <i className="ri-magic-line"></i>Créer depuis les scènes
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {clips.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2 sm:gap-3 bg-background-50 border border-background-200/70 rounded-lg px-2.5 py-2">
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-xs font-bold flex-shrink-0">
                {pad(i + 1)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800 truncate">{sceneLabel(c.scene_id)}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-foreground-400">
                  <span className="inline-flex items-center gap-1"><i className="ri-timer-line"></i>{formatDuration(Math.round((c.duration_ms || 0) / 1000))}</span>
                  <span className="hidden sm:inline-flex items-center gap-1">
                    <i className="ri-scissors-cut-line"></i>
                    {(c.start_ms / 1000).toFixed(1)}s → {(c.end_ms / 1000).toFixed(1)}s
                  </span>
                </div>
              </div>

              {/* Trim */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => applyTrim(c, c.start_ms + 500, c.end_ms)}
                  disabled={busy}
                  title="Rentrer la tête de clip (+0,5 s)"
                  className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  <i className="ri-contract-left-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => applyTrim(c, c.start_ms, c.end_ms - 500)}
                  disabled={busy}
                  title="Rentrer la queue de clip (−0,5 s)"
                  className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  <i className="ri-contract-right-line text-sm"></i>
                </button>
              </div>

              {/* Ordre */}
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => handleMove(i, -1)}
                  disabled={i === 0 || busy}
                  className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-700 disabled:opacity-30 transition-colors cursor-pointer"
                  title="Monter"
                >
                  <i className="ri-arrow-up-s-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => handleMove(i, 1)}
                  disabled={i === clips.length - 1 || busy}
                  className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-700 disabled:opacity-30 transition-colors cursor-pointer"
                  title="Descendre"
                >
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
              </div>

              <button
                type="button"
                onClick={() => handleDuplicate(c, i)}
                disabled={busy}
                title="Dupliquer le clip"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 disabled:opacity-40 transition-colors cursor-pointer"
              >
                <i className="ri-file-copy-line text-sm"></i>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(c)}
                title="Supprimer le clip"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddBlank}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-background-300/70 text-xs font-medium text-foreground-500 hover:border-accent-300 hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Ajouter un clip (écran-titre / générique)
          </button>
        </div>
      )}
    </div>
  );
}